/**
 * The `live-shaped` synthetic profile (plan §1.2 · §4.7).
 *
 * The demo dataset is uniformly populated: every P/E is a number, every window
 * carries a return, consensus is seeded and ρ is clamped into [-0.85, 0.95]. A
 * live backend is none of those things. Running the screens only against
 * demo-fidelity data therefore leaves the whole null-handling surface — empty
 * cells, empty states, out-of-band correlations — first exercised in
 * production. The rules themselves (moduli, history index, out-of-clamp pairs)
 * live in `core/src/constants/live-shape.ts` and are re-exported below;
 * `scripts/extract-demo-fixtures-lib.ts` imports that same module, and takes
 * WINDOW_KEYS from `contracts/src/enums.ts`, so the runtime adapter and the
 * committed fixtures cannot describe two different "live-shaped".
 *
 * ── Where the transform is applied, and why ───────────────────────────────────
 *
 * `DerivedSecurity` deliberately NARROWS `pe`/`div`/`ret`/`dd` to non-null (see
 * `universe/derive-security.ts`): the synthetic path cannot produce a null, so
 * inheriting the wire nullability there would make every synthetic consumer
 * carry an unreachable branch — and `?? 0` is what gets written to silence one.
 * Live-shaped needs nulls, so it has two possible seams:
 *
 *   (a) widen the row type feeding the six `local/` builders, or
 *   (b) shape the EMITTED WIRE PAYLOADS, where `Security.pe` is already
 *       `number | null` and `Security.ret[w]` already `number | null`.
 *
 * (b) is what this module does. (a) would relax the narrowing for every
 * consumer of the derivation in order to serve one profile, and would touch the
 * frozen `UNIVERSE` derivation the golden digest is computed over. (b) needs no
 * cast at all — the wire types are nullable already — and leaves `demo-fidelity`
 * literally the undecorated repository, so "the default is unchanged" is true by
 * construction rather than by test.
 *
 * The price of (b), stated rather than hidden. Every SELECTION the builders make
 * — rankings, breadth, cap-weighted aggregates, the correlation shortlists — is
 * made before the shaped values exist, so:
 *
 *   · a nulled row keeps the rank its underlying value earned instead of sorting
 *     to the tail the way SQL's NULLS LAST would place it, and
 *   · a declared out-of-clamp pair reaches the sector MATRIX (which is a
 *     projection, not a shortlist) but reaches `most`/`least` and the three
 *     diversification groups only when the pair survives selection on its
 *     PRE-override ρ. Over the full universe neither declared pair does.
 *
 * Both are acceptable because the profile's purpose is field shape in the UI;
 * aggregate arithmetic over genuine nulls is `core`'s own concern and is covered
 * by the analytics suites. What is NOT acceptable is two values for one pair, so
 * the override is applied to every emitted member as well as to the matrix — a
 * pair that does surface reads the same in both views.
 *
 * The plan is computed ONCE from the full row set and keyed by ticker, never
 * recomputed per payload: an index-derived rule applied to a filtered payload
 * would null AAPL on the dashboard and populate it in the DJI view — one
 * security with two sets of fundamentals, which no backend can produce.
 */
import {
  WINDOW_KEYS,
  type CorrelationMatrix,
  type OhlcCoverage,
  type OhlcSeries,
  type Security,
  type SecurityProfile,
  type SeriesStats,
  type StockFinancials,
} from '@meridian/contracts';

import {
  LIVE_SHAPE_EMPTY_STATEMENTS_MODULUS,
  LIVE_SHAPE_INSUFFICIENT_HISTORY_INDEX,
  LIVE_SHAPE_NULL_AVG_VOLUME_MODULUS,
  LIVE_SHAPE_NULL_DIV_MODULUS,
  LIVE_SHAPE_NULL_FINANCIALS_MODULUS,
  LIVE_SHAPE_NULL_PE_MODULUS,
  LIVE_SHAPE_NULL_SHARES_OUT_MODULUS,
  OUT_OF_CLAMP_SAMPLES,
  SYNTHETIC_PROFILES,
  type OutOfClampSample,
  type SyntheticProfile,
} from '../../constants/live-shape';

import { benchmarkOhlcFromCompleteSuffix } from './benchmark-ohlc';

import type { DatasetRepository } from '../../ports/dataset-repository';
import type { DerivedSecurity } from '../../universe/derive-security';

/*
 * The rule constants live in `constants/live-shape.ts`, which imports nothing,
 * and are re-exported here so every existing importer is unaffected. The split
 * exists because `scripts/extract-demo-fixtures-lib.ts` runs under
 * `node --experimental-strip-types` and cannot resolve this module's own
 * `@meridian/contracts` import — see that file's header.
 */
export {
  LIVE_SHAPE_EMPTY_STATEMENTS_MODULUS,
  LIVE_SHAPE_INSUFFICIENT_HISTORY_INDEX,
  LIVE_SHAPE_NULL_AVG_VOLUME_MODULUS,
  LIVE_SHAPE_NULL_DIV_MODULUS,
  LIVE_SHAPE_NULL_FINANCIALS_MODULUS,
  LIVE_SHAPE_NULL_PE_MODULUS,
  LIVE_SHAPE_NULL_SHARES_OUT_MODULUS,
  OUT_OF_CLAMP_SAMPLES,
  SYNTHETIC_PROFILES,
  type OutOfClampSample,
  type SyntheticProfile,
};

/**
 * Every 29th row loses ONLY `vol`/`beta` — the ADR-0022 shape the history rule
 * above cannot produce.
 *
 * The two rules describe different live statements. `UNIVERSE_SQL` reads
 * `ann_vol`/`beta_vs_spx` from the ACTIVE-WINDOW join, while `ret`/`dd` come
 * from an aggregate over EVERY window, so §6.5b's per-window nulling routinely
 * emits a security whose 1M/3M/1Y metrics are all real and whose 3Y `ann_vol`
 * and `beta_vs_spx` are null — the inhabitant ADR-0022 opens with, and the
 * dominant null shape in production. `shapeRow`'s history rule nulls the four
 * windows at the same time, so that MIXED row (a null `vol` beside a populated
 * `ret['1M']`) was unreachable in local mode: neither the dual-mode E2E run nor
 * `apps/api/test/parity.test.ts` ever exercised it.
 *
 * Coprime with 17 and 23 for the same reason those two are coprime with each
 * other, and index 0 is excluded so the two rules stay disjoint and separately
 * countable — a row cannot be both "no history at all" and "metrics only".
 *
 * Declared HERE rather than in `constants/live-shape.ts` because
 * `scripts/extract-demo-fixtures-lib.ts` cannot mirror it: the fixture row shape
 * carries `vol`/`beta`, but that script's `mapLiveUniverseRow` does not null them
 * even for the history row (the committed `live-shaped/universe.json` reports
 * AAPL with `vol: 0.19` while the runtime adapter emits `null`), so the fixtures
 * already characterise the pre-ADR-0022 shape. Putting this constant in the
 * shared module would advertise a mirroring that does not exist. Closing that
 * gap is an extract-script change, tracked separately.
 */
export const LIVE_SHAPE_NULL_METRICS_MODULUS = 29;

interface LiveShapePlan {
  readonly nullPe: ReadonlySet<string>;
  readonly nullDiv: ReadonlySet<string>;
  readonly noHistory: ReadonlySet<string>;
  /** ADR-0034 empty states: no servable snapshot / unknown float / no volume. */
  readonly nullFinancials: ReadonlySet<string>;
  readonly nullSharesOut: ReadonlySet<string>;
  readonly nullAvgVolume: ReadonlySet<string>;
  /** ADR-0035 empty-honest statements beside populated ratios. */
  readonly emptyStatements: ReadonlySet<string>;
  /** Null `vol`/`beta` with windows INTACT — the active-window-only null shape. */
  readonly noMetrics: ReadonlySet<string>;
  /** Unordered-pair key → ρ. Unordered IS the symmetry guarantee: ρ(a,b) ≡ ρ(b,a). */
  readonly rho: ReadonlyMap<string, number>;
}

function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

function planRho(tickers: ReadonlySet<string>): ReadonlyMap<string, number> {
  return new Map(
    OUT_OF_CLAMP_SAMPLES.filter((s) => tickers.has(s.a) && tickers.has(s.b)).map((s) => [
      pairKey(s.a, s.b),
      s.rho,
    ]),
  );
}

/* The shared shape of the three ADR-0034 rules: plain index modulus, no
   value-based disjunction. Kept out of the forEach below so that function's
   branch count stays under the complexity ceiling. */
function byModulus(rows: readonly DerivedSecurity[], modulus: number): ReadonlySet<string> {
  return new Set(rows.filter((_row, i) => i % modulus === 0).map((row) => row.ticker));
}

function planLiveShape(rows: readonly DerivedSecurity[]): LiveShapePlan {
  const nullPe = new Set<string>();
  const nullDiv = new Set<string>();
  const noHistory = new Set<string>();
  const noMetrics = new Set<string>();
  rows.forEach((row, i) => {
    /*
     * `||`, not `&&`. The conjunction is empty in practice — no row satisfies
     * both — so live-shaped, whose whole purpose is forcing production-shaped
     * nulls, would never exercise the null path and code treating a missing
     * fundamental as 0 would pass every test (R2b).
     */
    if (row.pe === 0 || i % LIVE_SHAPE_NULL_PE_MODULUS === 0) nullPe.add(row.ticker);
    if (row.div === 0 || i % LIVE_SHAPE_NULL_DIV_MODULUS === 0) nullDiv.add(row.ticker);
    if (i === LIVE_SHAPE_INSUFFICIENT_HISTORY_INDEX) noHistory.add(row.ticker);
    /*
     * `i > 0` is what keeps this disjoint from `noHistory`, whose index IS 0 —
     * a row must be either "no history at all" or "metrics only", never both,
     * or the two null shapes stop being separately countable.
     */
    if (i > 0 && i % LIVE_SHAPE_NULL_METRICS_MODULUS === 0) noMetrics.add(row.ticker);
  });
  return {
    nullPe,
    nullDiv,
    noHistory,
    noMetrics,
    nullFinancials: byModulus(rows, LIVE_SHAPE_NULL_FINANCIALS_MODULUS),
    nullSharesOut: byModulus(rows, LIVE_SHAPE_NULL_SHARES_OUT_MODULUS),
    nullAvgVolume: byModulus(rows, LIVE_SHAPE_NULL_AVG_VOLUME_MODULUS),
    emptyStatements: byModulus(rows, LIVE_SHAPE_EMPTY_STATEMENTS_MODULUS),
    rho: planRho(new Set(rows.map((r) => r.ticker))),
  };
}

type WindowNumberMap = Security['ret'];

/** Every key nulled, driven off WINDOW_KEYS so a fifth window cannot be missed. */
function nullifyWindows(map: WindowNumberMap): WindowNumberMap {
  return WINDOW_KEYS.reduce<WindowNumberMap>((acc, window) => ({ ...acc, [window]: null }), {
    ...map,
  });
}

/** New row every time — the input is another payload's object, never mutated. */
function shapeRow(row: Security, plan: LiveShapePlan): Security {
  const pe = plan.nullPe.has(row.ticker) ? null : row.pe;
  const div = plan.nullDiv.has(row.ticker) ? null : row.div;
  /* Live derives the float from a vendor field that is genuinely absent for
     some names; synthetic always computes it from mcap/price (R2b). */
  const sharesOut = plan.nullSharesOut.has(row.ticker) ? null : row.sharesOut;
  if (!plan.noHistory.has(row.ticker)) {
    if (!plan.noMetrics.has(row.ticker)) return { ...row, pe, div, sharesOut };
    /*
     * The ADR-0022 inhabitant that DOMINATES production and was previously
     * unreachable here: `ret`/`dd` stay intact while the active window's
     * `vol`/`beta` are null. On the live path `ann_vol`/`beta_vs_spx` come from
     * the ACTIVE-window join while `ret`/`dd` aggregate every window, so §6.5b
     * nulls the two metrics for one window without touching the others. The
     * `noHistory` rule below nulls all four together; only this rule produces
     * the mixed shape, so `null` beside a populated `ret['1M']` is now reachable
     * in local mode and the dual-mode E2E run exercises it.
     */
    return { ...row, pe, div, sharesOut, vol: null, beta: null, insufficientHistory: true };
  }
  /* ADR-0022: insufficient history nulls the wire projections of ann_vol and
     beta_vs_spx as well as ret/dd — same inhabitant as live HTTP, so local E2E
     can exercise em-dash cells that production emits. */
  return {
    ...row,
    pe,
    div,
    sharesOut,
    vol: null,
    beta: null,
    insufficientHistory: true,
    ret: nullifyWindows(row.ret),
    dd: nullifyWindows(row.dd),
  };
}

/**
 * ADR-0034 `financials: null` — the inhabitant live v1 emits for every name
 * until the ratio/income CSVs are staged, and previously unreachable here.
 */
function shapeFinancials(
  financials: StockFinancials | null,
  security: Security,
  plan: LiveShapePlan,
): StockFinancials | null {
  if (financials === null || plan.nullFinancials.has(security.ticker)) return null;
  const statements = plan.emptyStatements.has(security.ticker)
    ? { ...financials, balanceSheet: [], cashFlow: [] }
    : financials;
  if (security.pe !== null) return statements;
  /*
   * Built upstream from the RAW `pe` (adapters/local/security.ts), so a row
   * whose P/E this decorator just nulled would otherwise still ship a `peFwd`
   * and `epsTtm` derived from the value the payload no longer admits to having.
   */
  return { ...statements, ratios: { ...statements.ratios, epsTtm: null, peFwd: null } };
}

/** `avgVolume: null` is "no observation"; 0 is a real halted window (R2b). */
function shapeStats(stats: SeriesStats, ticker: string, plan: LiveShapePlan): SeriesStats {
  return plan.nullAvgVolume.has(ticker) ? { ...stats, avgVolume: null } : stats;
}

/* Returns a MUTABLE array deliberately: the contracts schemas infer
   `rows`/`members`/`movers` as mutable, so a `readonly` return here fails to
   assign into the payload. Narrowing it would be a contracts change, not a
   local one. `.map()` already yields a fresh array, so nothing is shared. */
function shapeRows(rows: readonly Security[], plan: LiveShapePlan): Security[] {
  return rows.map((row) => shapeRow(row, plan));
}

function shapeMatrix(matrix: CorrelationMatrix, plan: LiveShapePlan): CorrelationMatrix {
  return {
    ...matrix,
    values: matrix.values.map((row, i) => {
      const a = matrix.tickers[i];
      return row.map((rho, j) => {
        const b = matrix.tickers[j];
        /* GUARDED, not silently passed through: the matrix comes from
           buildSectorDetail, so a values/tickers dimension mismatch is another
           module's behaviour changing under us (style §4 — state local
           invariants, guard cross-module ones). Returning the underlying ρ
           there would emit an un-overridden pair with no signal at all. */
        if (a === undefined || b === undefined) {
          throw new Error(
            `live-shaped: correlation matrix is ragged at [${String(i)}][${String(j)}]`,
          );
        }
        return plan.rho.get(pairKey(a, b)) ?? rho;
      });
    }),
  };
}

interface RhoMember {
  readonly ticker: string;
  readonly rho: number;
}

function shapeMembers<T extends RhoMember>(
  members: readonly T[],
  subject: string,
  plan: LiveShapePlan,
): T[] {
  return members.map((member) => {
    const rho = plan.rho.get(pairKey(subject, member.ticker));
    return rho === undefined ? member : { ...member, rho };
  });
}

interface GroupedPayload {
  readonly members: readonly Security[];
  readonly movers: readonly Security[];
  readonly matrix?: CorrelationMatrix | undefined;
}

/** Members, movers, and matrix (when present) share one pair-override plan. */
function shapeGroupedPayload<T extends GroupedPayload>(detail: T, plan: LiveShapePlan): T {
  const members = shapeRows(detail.members, plan);
  const movers = shapeRows(detail.movers, plan);
  const matrix = detail.matrix;
  if (matrix === undefined) return { ...detail, members, movers };
  return { ...detail, members, movers, matrix: shapeMatrix(matrix, plan) };
}

/**
 * Decorate a demo-fidelity repository with production field shape.
 *
 * A decorator rather than a seventh payload builder: every rule here is
 * cross-cutting (the same ticker must be null in every payload that carries
 * securities), so pushing it into the builders would mean N copies of one rule.
 * `getMeta`, `getSeries`, `getBenchmarkSeries`, `getOhlcSeries` and
 * `getBenchmarkOhlcSeries` pass straight through — `dataSource: 'synthetic'`
 * is a SAFETY classification that live-shaped must not disguise, and bar
 * values are already what a vendor would report. Area *list* tiles are
 * aggregates, so `getAreas` is pass-through; `getArea` members/movers/matrix
 * shape like sector. Tests may opt `getBenchmarkOhlcSeries` into the empty
 * complete-suffix arm via `benchmarkOhlcCompleteSuffix`.
 */
interface LiveShapedOptions {
  readonly benchmarkOhlcCompleteSuffix?: {
    readonly series: OhlcSeries | null;
    readonly examined: OhlcCoverage | null;
  };
}

function benchmarkOhlcMethod(
  base: DatasetRepository,
  options: LiveShapedOptions | undefined,
): DatasetRepository['getBenchmarkOhlcSeries'] {
  const suffix = options?.benchmarkOhlcCompleteSuffix;
  if (suffix === undefined) return base.getBenchmarkOhlcSeries;
  return () => Promise.resolve(benchmarkOhlcFromCompleteSuffix(suffix.series, suffix.examined));
}

/** Extracted so `createLiveShapedRepository` stays under the line ceiling. */
function shapeProfile(profile: SecurityProfile, plan: LiveShapePlan): SecurityProfile {
  const security = shapeRow(profile.security, plan);
  return {
    ...profile,
    security,
    stats: shapeStats(profile.stats, security.ticker, plan),
    /* Live v1 licenses no consensus provider and invents no headlines about
       real companies, so both empty states must be reachable locally. */
    consensus: null,
    catalysts: [],
    financials: shapeFinancials(profile.financials, security, plan),
  };
}

export function createLiveShapedRepository(
  base: DatasetRepository,
  rows: readonly DerivedSecurity[],
  options?: LiveShapedOptions,
): DatasetRepository {
  const plan = planLiveShape(rows);
  return {
    getMeta: base.getMeta,
    getSeries: base.getSeries,
    getBenchmarkSeries: base.getBenchmarkSeries,
    getOhlcSeries: base.getOhlcSeries,
    getBenchmarkOhlcSeries: benchmarkOhlcMethod(base, options),
    getMarketSnapshot: base.getMarketSnapshot,
    /* Passed through unshaped: the live shape describes what a VENDOR reports
       about a security — null fundamentals, missing metrics. A book is what the
       operator typed, so live mode changes where it comes from, never its
       field shape, and there is nothing here to degrade. */
    getBook: base.getBook,

    getDashboard: async (query) => {
      const payload = await base.getDashboard(query);
      return { ...payload, rows: shapeRows(payload.rows, plan) };
    },

    getAreas: async (query) => base.getAreas(query),

    /* `diversifiers` carries `meanRho` — a mean over every sector member, so a
       single pair override is not expressible in it. Left as derived. */
    getSector: async (id, query) => shapeGroupedPayload(await base.getSector(id, query), plan),

    getArea: async (id, query) => shapeGroupedPayload(await base.getArea(id, query), plan),

    getSecurity: async (ticker, query) => shapeProfile(await base.getSecurity(ticker, query), plan),

    getPeers: async (ticker, query) => {
      const payload = await base.getPeers(ticker, query);
      return {
        sector: shapeRows(payload.sector, plan),
        industry: shapeRows(payload.industry, plan),
        industryId: payload.industryId,
        industryName: payload.industryName,
      };
    },

    getDiversifiers: async (ticker, query) => {
      const payload = await base.getDiversifiers(ticker, query);
      /* Keyed off the RESOLVED subject, not the requested ticker: an alias would
         otherwise pair the override against a name the payload never names. */
      const subject = payload.ticker;
      return {
        ...payload,
        groups: payload.groups.map((group) => ({
          ...group,
          members: shapeMembers(group.members, subject, plan),
        })),
        most: shapeMembers(payload.most, subject, plan),
        least: shapeMembers(payload.least, subject, plan),
      };
    },

    search: async (query) => shapeRows(await base.search(query), plan),
  };
}
